import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { token, type } from '../theme/tokens.js';

export type EyebrowTone = 'muted' | 'amber' | 'blue' | 'alert';

/**
 * What the eyebrow renders as.
 *
 * `span` by default, because most eyebrows label a value rather than open a
 * section — and a heading that titles nothing is worse than no heading.
 */
export type EyebrowAs = 'span' | 'h2' | 'h3';

export interface EyebrowProps {
  children: ReactNode;
  tone?: EyebrowTone;
  /** Show a leading status dot. Use for live/active states. */
  showDot?: boolean;
  /**
   * Render as a real heading when this eyebrow **opens a section** rather than
   * labelling a value.
   *
   * A surface built of several sections needs an outline somebody can navigate,
   * and the eyebrow is already what titles those sections — so it is what has to
   * be able to become the heading. A second component with the same treatment
   * and different semantics would be two ways to write one thing.
   *
   * Defaults to `span`, so every existing caller stays out of the outline.
   */
  as?: EyebrowAs;
  className?: string;
}

const TONE_COLOR: Record<EyebrowTone, string> = {
  muted: token('bp-text-muted'),
  amber: token('bp-amber'),
  blue: token('bp-blue-text'),
  alert: token('bp-danger'),
};

/**
 * Uppercase micro-label with wide tracking.
 *
 * This is the design system's signature metadata treatment —
 * `TRANSCRIBED`, `02 ACTIVE NOTES`, `OFFICIAL CERTIFICATION REQUIRED`. The
 * component owns the casing so callers cannot get it wrong: pass normal copy,
 * get an uppercase label.
 *
 * Counts are zero-padded in eyebrows by convention (`02 ACTIVE SPACES`) — that
 * is the caller's job, since only the caller knows the number.
 */
export function Eyebrow({
  children,
  tone = 'muted',
  showDot = false,
  as: Tag = 'span',
  className,
}: EyebrowProps) {
  return (
    <Tag
      className={cn('inline-flex items-center gap-2', className)}
      style={{
        // Casing is set inline, not via a utility class, so the rule holds in
        // any consumer — including one without Tailwind loaded.
        textTransform: 'uppercase',
        color: TONE_COLOR[tone],
        fontSize: type.size['2xs'],
        fontWeight: type.weight.semibold,
        letterSpacing: type.tracking.wide,
      }}
    >
      {showDot ? (
        // size-1.5 is 6px on Tailwind's 0.25rem scale — on-system, no raw px.
        <span
          aria-hidden="true"
          className="inline-block size-1.5 shrink-0 rounded-full"
          style={{ background: TONE_COLOR[tone] }}
        />
      ) : null}
      {children}
    </Tag>
  );
}
